import {
  ExclamationCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PoweroffOutlined,
  RocketOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { Divider, Space } from 'antd';
import { message } from '@/components/Message';
import React, { useContext, useMemo, useState } from 'react';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { AppointmentStatus } from '@/models/appointment';
import { Perms } from '@/models/perms';
import {
  assignOrderToUser,
  AssignOrderToUserResult,
  OrderDetail,
  OrderOperationHelper,
  OrderStatusMap,
  OrderType,
  refundOrder,
  useCompleteOrder,
} from '@/models/order';
import { PaymentChannel } from '@/models/payment';
import { useUserFullInfo } from '@/models/user';
import Modal from '../Modal';
import PosButton from '../PosButton';
import Price from '../Price';
import { emitGlobalEvent } from '../QRScanner/GlobalScanner';
import { PostInvalidate } from '../../../typings/global';
import { POSPaymentResponse } from '@/models/pos';
import { getPreOrderByOrderDetail } from '@/models/preOrder';

type Props = {
  orderDetail: OrderDetail;
  // onOrderDetailUpdate?: (orderDetail: OrderDetail) => void;
  updateOrderDetail: (f: (draft: OrderDetail) => void | OrderDetail) => void;
} & PostInvalidate<OrderDetail>;

const OrderOperation: React.FC<Props> = ({
  orderDetail,
  updateOrderDetail,
  postInvalidate,
}: Props) => {
  const { order, appointment, user } = orderDetail;
  const [{ externalContactWecomId }, , helpers] = useContext(BaseInfoContext);
  const { send: requestUserFullInfo } = useUserFullInfo();
  const { send: requestCompleteOrder } = useCompleteOrder();
  const [showRiskActions, setShowRiskActions] = useState(false);

  const refund = useMemo(() => {
    // 订单状态
    if (OrderOperationHelper.canOrderRefund(order)) {
      // 权限
      if (helpers.hasPermission(Perms.ORDER_REFUND)) {
        // 类型
        if (order.orderType !== OrderType.Recharge) {
          return (
            <PosButton
              danger
              key="refund"
              icon={<RollbackOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: '确认退款吗？',
                  icon: <ExclamationCircleOutlined />,
                  content:
                    order.payChannel === PaymentChannel.Cash ? (
                      ''
                    ) : (
                      <Space direction={'vertical'} size={2}>
                        <div>
                          订单实付金额 <Price value={order.actualPrice} />
                          {''}
                          元将原路退回客户支付账户
                        </div>
                        <div>
                          请确保关联
                          <b>
                            {order.payChannel === PaymentChannel.Alipay ? '支付宝' : '微信'}
                            账户
                          </b>
                          有足够余额
                        </div>
                      </Space>
                    ),
                  onOk: () => {
                    return new Promise((resolve, reject) => {
                      refundOrder(order)
                        .then(updateOrder => {
                          message.success(`退款成功`);
                          // onOrderUpdate(updateOrder);
                          updateOrderDetail(draft => {
                            draft.order = updateOrder;
                          });
                          resolve(undefined);
                        })
                        .catch(e => {
                          message.error(e.msg);
                          reject();
                        });
                    });
                  },
                });
              }}>
              退款
            </PosButton>
          );
        }
      }
    }
    return null;
  }, [order]);

  const statusAction = useMemo(() => {
    // 完成服务
    const completeOrder = OrderOperationHelper.canComplete(order) ? (
      <PosButton
        key={'completeOrder'}
        type="primary"
        icon={<PoweroffOutlined />}
        onClick={async () => {
          Modal.confirm({
            title: '确认操作',
            content: '请确认服务已完成，确认后订单将结束',
            okText: '服务已完成',
            onOk: async () => {
              const updatedOrder = await requestCompleteOrder({
                orderSn: order.orderSn,
              });

              // 更新订单+预约单状态
              updateOrderDetail(draft => {
                draft.order.orderStatus = OrderStatusMap.Complete.type;
                if (draft.appointment) {
                  draft.appointment.status = AppointmentStatus.COMPLETE;
                }
              });

              emitGlobalEvent({
                type: 'actionCompleteOrder',
                payload: {
                  order: updatedOrder,
                  user: user,
                  skuList: orderDetail.skuList,
                  transactionList: orderDetail.transactionList,
                } as POSPaymentResponse,
              });
              helpers.closeOrderModal();
              message.success('操作成功，服务已完成');
            },
          });
        }}>
        完成服务，结束订单
      </PosButton>
    ) : null;

    const startOrder = OrderOperationHelper.canStart(order) ? ( // 开始服务
      (<PosButton
        key={`startOrder`}
        icon={<RocketOutlined />}
        type="primary"
        onClick={async () => {
          // Modal.confirm({
          //   title: '确认操作',
          //   content: '请仔细核对服务项目后确定开始服务',
          //   okText: '开始服务',
          //   onOk: async () => {
          // 1. 流转订单状态 + 流转预约单状态
          // 2. 拉取用户全量信息
          // 3. 汇总用户全量信息 + 订单商品信息到收银台， 添加到服务中列表
          // await requestStartOrder({ orderId: order.id, id: appointment?.id });

          // 更新订单+预约单状态
          // updateOrderDetail(draft => {
          //   draft.order.orderStatus = OrderStatusMap.Progressing.type;
          //   if (draft.appointment) {
          //     draft.appointment.status = AppointmentStatus.CHECKIN;
          //   }
          // });

          const userFullInfo = await requestUserFullInfo({ userId: order.userId });
          const preOrder = getPreOrderByOrderDetail({ userFullInfo, orderDetail, appointment });
          emitGlobalEvent({
            type: 'actionStartOrder',
            payload: preOrder,
          });
          helpers.closeOrderModal();
          message.success('操作成功');
          //   },
          // });
        }}>创建服务
              </PosButton>)
    ) : null;

    return [startOrder, completeOrder].filter(Boolean);
  }, [order, appointment]);

  const riskActions = [refund].filter(Boolean);

  const others = [
    user && !user.weixinUnionid && Boolean(externalContactWecomId) ? (
      <PosButton
        key="bindorder"
        onClick={() => {
          Modal.confirm({
            title: '操作确认',
            content: '订单关联的原客户数据将迁移至当前客户',
            onOk: async () => {
              const { code } = await assignOrderToUser({
                orderSn: order.orderSn,
                wecomId: externalContactWecomId,
              });
              if (code >= 200) {
                message.success(`操作成功`);
                postInvalidate();
              } else {
                message.error(`失败: ${AssignOrderToUserResult[code]}`);
              }
            },
          });
        }}>
        归属当前客户
      </PosButton>
    ) : null,
  ].filter(Boolean);

  return (
    <Space wrap>
      {riskActions.length ? (
        <>
          <PosButton
            type="text"
            danger
            icon={showRiskActions ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={() => {
              setShowRiskActions(!showRiskActions);
            }}>
            {showRiskActions ? '收起' : '展开'}风险操作
          </PosButton>
          <Divider type="vertical" style={showRiskActions ? { borderColor: 'red' } : undefined} />
        </>
      ) : null}
      {showRiskActions ? riskActions : null}
      {statusAction}
      {others}
    </Space>
  );
};

export default OrderOperation;
